import { computed, watch } from '@vue/composition-api'

import { useAuth } from '@/composables/useAuth'
import { useCentrifugo } from '@/composables/useCentrifugo'
import { ORDER_VIEW_PERMISSION } from '@/constants/permissions'
import router from '@/router'
import store from '@/store'

const PLAY_PRESENCE_CHANNEL = 'play:presence'
let personalChannel = null
let presenceSubscription = null
let stopRouteHook = null
let initialized = false
let stateRequest = null

export const ensurePlayPresence = async () => {
    const { ready, subscribe } = useCentrifugo()
    if (!initialized) throw new Error('Сервис Play не инициализирован')

    await ready()
    if (!presenceSubscription) {
        presenceSubscription = subscribe(PLAY_PRESENCE_CHANNEL)
    }
    if (!presenceSubscription) {
        throw new Error('Не удалось подписаться на распределение заказов')
    }

    await presenceSubscription.ready(5000)
    return presenceSubscription
}

export const releasePlayPresence = () => {
    if (!presenceSubscription) return

    const { unsubscribe } = useCentrifugo()
    unsubscribe(PLAY_PRESENCE_CHANNEL)
    presenceSubscription = null
}

export function usePlayNotifications() {
    const { userLogin, hasPermission } = useAuth()
    const { connect, subscribe, unsubscribe, isConnected } = useCentrifugo()
    const playEnabled = computed(() => store.state.play.enabled)

    const isPlayRoute = () => router.currentRoute.meta.isOrderList === true

    const handlePublication = data => {
        console.debug('[WS: Play] Получено событие Play')
        store.dispatch('play/handleEvent', data)
    }

    const syncPresence = () => {
        if (!initialized || !isConnected.value) return

        if (!isPlayRoute()) {
            releasePlayPresence()
            return
        }

        if (playEnabled.value && !presenceSubscription) {
            ensurePlayPresence().catch(error => {
                console.error('[WS: Play] Ошибка подписки на presence', error)
            })
        } else if (!playEnabled.value && presenceSubscription) {
            releasePlayPresence()
        }
    }

    const fetchState = () => {
        if (!initialized || !isConnected.value || stateRequest) return

        stateRequest = store
            .dispatch('play/fetchState')
            .catch(() => null)
            .finally(() => {
                stateRequest = null
                syncPresence()
            })
    }

    const reset = () => {
        initialized = false
        if (personalChannel) unsubscribe(personalChannel)
        releasePlayPresence()
        if (stopRouteHook) stopRouteHook()
        stopRouteHook = null
        personalChannel = null
        stateRequest = null
        store.commit('play/RESET_STATE')
    }

    const onRouteChange = to => {
        if (!initialized) return

        if (to.meta.isOrderList === true) {
            fetchState()
            return
        }

        releasePlayPresence()
        store.commit('play/RESET_STATE')
    }

    const init = () => {
        const login = userLogin.value
        if (!login) return
        if (!hasPermission(ORDER_VIEW_PERMISSION)) return

        reset()
        initialized = true
        personalChannel = `play:${login}`
        stopRouteHook = router.afterEach(onRouteChange)
        connect()
        subscribe(personalChannel, handlePublication)

        if (isConnected.value) fetchState()
    }

    watch(isConnected, connected => {
        if (connected) fetchState()
        else presenceSubscription = null
    })

    watch(playEnabled, syncPresence)

    return {
        initPlayNotifications: init,
        resetPlayNotifications: reset,
    }
}
